import { api } from './index';
import type { TaxDocumentFailureReason, TaxDocumentKind, TaxDocumentStatus } from './charge-closures';

const BASE_PATH = '/api/internal/tax_documents';

export interface TaxDocument {
  id: number;
  sourceType: string;
  sourceId: number;
  countryCode: string;
  kind: TaxDocumentKind;
  documentCode: string;
  series: string | null;
  number: string | null;
  status: TaxDocumentStatus;
  issuedOn: string | null;
  currency: string;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  roundingDeltaCents: number;
  withholdingCents: number;
  // Issued by the payer on the supplier's behalf (a self-billed document).
  selfBilled: boolean;
  parties: TaxDocumentParty[];
  failureReason: TaxDocumentFailureReason;
  submittedAt: string | null;
  acceptedAt: string | null;
  deliveredAt: string | null;
  pdfAvailable: boolean;
  reversibleUntil: string | null;
  references: TaxDocumentReference[];
  createdAt: string;
}

// A party as it was when the document was built: issuer, recipient and, on a
// self-billed document, the supplier who rendered the service.
export interface TaxDocumentParty {
  role: 'issuer' | 'recipient' | 'supplier';
  kind: 'person' | 'organization';
  taxIdScheme: string | null;
  taxId: string | null;
  name: string | null;
  email: string | null;
}

// What a document points at: a credit note names the invoice it voids.
export interface TaxDocumentReference {
  id: number;
  kind: 'voids' | 'corrects';
  documentKind: TaxDocumentKind;
  documentCode: string;
  number: string | null;
  status: TaxDocumentStatus;
}

export interface TaxDocumentLine {
  id: number;
  lineNumber: number;
  description: string;
  quantity: number;
  currency: string;
  unitPriceCents: number;
  lineCents: number;
  taxCategory: string;
  taxRate: string;
  taxCents: number;
}

export interface TaxDocumentDetail {
  document: TaxDocument;
  lines: TaxDocumentLine[];
}

export const taxDocumentsApi = {
  // Builds the document for an approved closure and queues its emission; the
  // response is the draft, the number arrives asynchronously.
  create(chargeClosureId: number): Promise<TaxDocument> {
    return api({
      method: 'post',
      url: `/api/internal/charge_closures/${chargeClosureId}/tax_documents`,
    }).then(response => response.data.taxDocument);
  },
  show(id: number): Promise<TaxDocumentDetail> {
    return api({
      method: 'get',
      url: `${BASE_PATH}/${id}`,
    }).then(response => ({ document: response.data.taxDocument, lines: response.data.meta.lines }));
  },
  retry(id: number): Promise<TaxDocument> {
    return api({
      method: 'post',
      url: `${BASE_PATH}/${id}/retry`,
    }).then(response => response.data.taxDocument);
  },
  // Builds and queues the credit note that voids an accepted document; the
  // original is cancelled and its closure released once the authority accepts it.
  reverse(id: number): Promise<TaxDocument> {
    return api({
      method: 'post',
      url: `${BASE_PATH}/${id}/reverse`,
    }).then(response => response.data.taxDocument);
  },
  // Redirects to the stored legal PDF; never the provider's URL.
  pdfUrl(id: number): string {
    return `${BASE_PATH}/${id}/pdf`;
  },
};
